/**
 * Onboarding API
 *
 * Mock onboarding service for role-specific onboarding flows.
 * Based on Doc 12: Onboarding & Empty States
 */

import type {
  OnboardingProgress,
  OnboardingFlowType,
  GuidedTourStop,
  ApiResponse,
} from '../types'
import { mockDb, timestamp } from './mockDb'
import { safeExecute } from './client'

const SESSION_KEY = 'docurious-session'

function getCurrentUserId(): string | null {
  const stored = localStorage.getItem(SESSION_KEY)
  if (!stored) return null
  try {
    return JSON.parse(stored).userId
  } catch {
    return null
  }
}

/**
 * Guided tour configurations per role
 */
const GUIDED_TOURS: Record<string, GuidedTourStop[]> = {
  general: [
    { id: 'hero', targetSelector: '[data-tour="hero"]', title: 'Welcome to DoCurious!', description: 'This is your dashboard — see your progress, challenges, and badges at a glance.', position: 'bottom' },
    { id: 'stats', targetSelector: '[data-tour="stats"]', title: 'Your Stats', description: 'Track your XP, level, streak, and completed challenges here.', position: 'bottom' },
    { id: 'challenges', targetSelector: '[data-tour="challenges"]', title: 'Active Challenges', description: 'Your in-progress challenges are listed here. Click any to continue working.', position: 'bottom' },
    { id: 'quick-actions', targetSelector: '[data-tour="quick-actions"]', title: 'Quick Actions', description: 'Start new challenges, explore the catalog, or check your saved list.', position: 'left' },
    { id: 'recommended', targetSelector: '[data-tour="recommended"]', title: 'Recommended For You', description: 'Personalized challenge suggestions based on your interests and activity.', position: 'bottom' },
    { id: 'community-highlights', targetSelector: '[data-tour="community-highlights"]', title: 'Community Highlights', description: 'See updates from communities you belong to.', position: 'bottom' },
    { id: 'badges', targetSelector: '[data-tour="badges"]', title: 'Badges & Achievements', description: 'Earn badges by completing challenges and reaching milestones.', position: 'left' },
  ],
  student: [
    { id: 'hero', targetSelector: '[data-tour="hero"]', title: 'Welcome, Student!', description: 'This is your learning dashboard — see assignments, XP, and badges.', position: 'bottom' },
    { id: 'stats', targetSelector: '[data-tour="stats"]', title: 'Your Progress', description: 'Track your level, XP, and streak to keep your momentum going.', position: 'bottom' },
    { id: 'challenges', targetSelector: '[data-tour="challenges"]', title: 'Your Challenges', description: 'Assigned and self-chosen challenges appear here.', position: 'bottom' },
    { id: 'badges', targetSelector: '[data-tour="badges"]', title: 'Badges', description: 'Complete challenges to earn badges and level up!', position: 'left' },
  ],
  teacher: [
    { id: 'hero', targetSelector: '[data-tour="hero"]', title: 'Teacher Dashboard', description: 'See your classes, assignments, and student activity at a glance.', position: 'bottom' },
    { id: 'stats', targetSelector: '[data-tour="stats"]', title: 'Class Stats', description: 'Monitor student engagement and completion rates.', position: 'bottom' },
    { id: 'challenges', targetSelector: '[data-tour="challenges"]', title: 'Manage Challenges', description: 'Browse and assign challenges to your students.', position: 'bottom' },
    { id: 'quick-actions', targetSelector: '[data-tour="quick-actions"]', title: 'Quick Actions', description: 'Assign challenges, review submissions, or check class analytics.', position: 'left' },
  ],
  parent: [
    { id: 'hero', targetSelector: '[data-tour="hero"]', title: 'Parent Dashboard', description: "See your child's recent activity and progress at a glance.", position: 'bottom' },
    { id: 'stats', targetSelector: '[data-tour="stats"]', title: 'Child Stats', description: "Track your child's XP, level, and completed challenges.", position: 'bottom' },
    { id: 'challenges', targetSelector: '[data-tour="challenges"]', title: 'Active Challenges', description: "View what your child is currently working on.", position: 'bottom' },
    { id: 'quick-actions', targetSelector: '[data-tour="quick-actions"]', title: 'Approvals & Settings', description: 'Manage permissions and review pending requests.', position: 'left' },
  ],
  school_admin: [
    { id: 'hero', targetSelector: '[data-tour="hero"]', title: 'School Admin Dashboard', description: 'Overview of your school — students, teachers, and challenge activity.', position: 'bottom' },
    { id: 'stats', targetSelector: '[data-tour="stats"]', title: 'School Stats', description: 'Monitor enrollment, activity, and budget usage.', position: 'bottom' },
    { id: 'challenges', targetSelector: '[data-tour="challenges"]', title: 'Challenge Management', description: 'Manage challenge assignments and purchase requests.', position: 'bottom' },
    { id: 'quick-actions', targetSelector: '[data-tour="quick-actions"]', title: 'Admin Actions', description: 'Manage classes, roster, assignments, and school settings.', position: 'left' },
  ],
  vendor: [
    { id: 'hero', targetSelector: '[data-tour="hero"]', title: 'Vendor Dashboard', description: 'See your challenge performance, orders, and revenue at a glance.', position: 'bottom' },
    { id: 'stats', targetSelector: '[data-tour="stats"]', title: 'Performance Stats', description: 'Track views, completions, revenue, and ratings.', position: 'bottom' },
    { id: 'challenges', targetSelector: '[data-tour="challenges"]', title: 'Your Challenges', description: 'Manage and create challenges for the platform.', position: 'bottom' },
    { id: 'quick-actions', targetSelector: '[data-tour="quick-actions"]', title: 'Quick Actions', description: 'Create new challenges, manage orders, and view analytics.', position: 'left' },
  ],
}

/**
 * Onboarding API
 */
export const onboardingApi = {
  async getOnboardingState(flowType: OnboardingFlowType): Promise<ApiResponse<OnboardingProgress | null>> {
    return safeExecute(() => {
      const userId = getCurrentUserId()
      if (!userId) throw new Error('Not authenticated')

      const progress = mockDb.findOne<OnboardingProgress>(
        'onboardingProgress',
        (p) => p.userId === userId && p.flowType === flowType
      )
      return progress
    })
  },

  async initFlow(flowType: OnboardingFlowType, totalSteps: number): Promise<ApiResponse<OnboardingProgress>> {
    return safeExecute(() => {
      const userId = getCurrentUserId()
      if (!userId) throw new Error('Not authenticated')

      const existing = mockDb.findOne<OnboardingProgress>(
        'onboardingProgress',
        (p) => p.userId === userId && p.flowType === flowType
      )
      if (existing) return existing

      return mockDb.insert<OnboardingProgress>('onboardingProgress', {
        userId,
        flowType,
        currentStep: 0,
        totalSteps,
        completedSteps: [],
        data: {},
      } as unknown as OnboardingProgress)
    })
  },

  async updateStep(
    flowType: OnboardingFlowType,
    step: number,
    stepId: string,
    data: Record<string, unknown>
  ): Promise<ApiResponse<OnboardingProgress>> {
    return safeExecute(() => {
      const userId = getCurrentUserId()
      if (!userId) throw new Error('Not authenticated')

      const progress = mockDb.findOne<OnboardingProgress>(
        'onboardingProgress',
        (p) => p.userId === userId && p.flowType === flowType
      )
      if (!progress) throw new Error('Onboarding not started')

      const completedSteps = [...new Set([...progress.completedSteps, stepId])]
      const updatedData = { ...progress.data, [stepId]: data }

      return mockDb.update<OnboardingProgress>('onboardingProgress', progress.id, {
        currentStep: step,
        completedSteps,
        data: updatedData,
      } as Partial<OnboardingProgress>)!
    })
  },

  async completeFlow(flowType: OnboardingFlowType): Promise<ApiResponse<OnboardingProgress>> {
    return safeExecute(() => {
      const userId = getCurrentUserId()
      if (!userId) throw new Error('Not authenticated')

      const progress = mockDb.findOne<OnboardingProgress>(
        'onboardingProgress',
        (p) => p.userId === userId && p.flowType === flowType
      )
      if (!progress) throw new Error('Onboarding not started')

      return mockDb.update<OnboardingProgress>('onboardingProgress', progress.id, {
        completedAt: timestamp(),
      } as Partial<OnboardingProgress>)!
    })
  },

  async getGuidedTourStops(role: string): Promise<ApiResponse<GuidedTourStop[]>> {
    return safeExecute(() => {
      return GUIDED_TOURS[role] ?? []
    })
  },
}
